import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { isSuperAdmin } from "../hooks/useSuperAdmin";
import { toast } from "sonner";
import {
  Film, Calendar, Search, Plus, ChevronDown, ChevronRight, Clock,
  AlertTriangle, CheckCircle2, LayoutGrid, List, Building2, X, Flame,
  Users, BarChart3, Eye, Trash2, Upload
} from "lucide-react";
import UserAutocomplete from "../components/UserAutocomplete";
import FormCarousel from "../components/FormCarousel";
import useIsMobile from "../hooks/useIsMobile";

/* ═══════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════ */
const ASSET_TYPE_COLORS = {
  reel: "#3B82F6", ad_film: "#A855F7", static: "#22C55E",
  carousel: "#F59E0B", story: "#EC4899", thumbnail: "#06B6D4",
  performance: "#10B981",
};
const ASSET_TYPE_LABELS = {
  reel: "Reel", ad_film: "Ad Film", static: "Static",
  carousel: "Carousel", story: "Story", thumbnail: "Thumbnail",
  performance: "Performance",
};
const STATUS_PHASE = {
  "Concept": "pre", "Script": "pre", "Treatment": "pre", "Design Brief": "pre", "Brief": "pre",
  "Pre-Production": "pre", "Shoot Scheduled": "shoot", "Shooting": "shoot", "Design/Shoot": "shoot",
  "Edit": "post", "Color Grade": "post", "Sound Mix": "post", "Design V1": "post",
  "Internal Review": "review", "Client Review": "review", "Revisions": "review", "Review": "review",
  "Approved": "done", "Live": "done",
};
const PHASE_COLORS = { pre: "#64748B", shoot: "#F59E0B", post: "#3B82F6", review: "#A855F7", done: "#22C55E" };
const PHASE_LABELS = { pre: "Pre-Production", shoot: "Production", post: "Post-Production", review: "Review", done: "Complete" };

// Shoot day = four 3-hour slots, 9am→9pm. A shoot books 1–4 contiguous slots.
const DAY_START = 9, DAY_END = 21, NSLOT = 4, CAL_CELL = 96;
const SLOT_PX = CAL_CELL / NSLOT;
const SLOT_DEFS = [
  { k: 0, s: 9, e: 12, label: "9–12" },
  { k: 1, s: 12, e: 15, label: "12–3" },
  { k: 2, s: 15, e: 18, label: "3–6" },
  { k: 3, s: 18, e: 21, label: "6–9" },
];
const RAIL_MARKS = [9, 12, 15, 18, 21];
const hourLabel = (h) => { const ap = h >= 12 ? "pm" : "am"; let hh = h > 12 ? h - 12 : h; return hh + ap; };
const hourOf = (iso) => { const t = (iso || "").slice(11, 16); if (!t) return DAY_START; const [h, m] = t.split(":").map(Number); return h + (m || 0) / 60; };
const hhmm = (h) => `${String(Math.floor(h)).padStart(2, "0")}:${String(Math.round((h % 1) * 60)).padStart(2, "0")}:00`;

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
}

/* ═══════════════════════════════════════
   PRODUCTION DASHBOARD
   ═══════════════════════════════════════ */
export default function ProductionDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [showKpiDue, setShowKpiDue] = useState(false);
  const [myQueue, setMyQueue] = useState(null);
  const [stats, setStats] = useState(null);
  const [studios, setStudios] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [shoots, setShoots] = useState([]);
  const [dragShoot, setDragShoot] = useState(null);
  const [loading, setLoading] = useState(true);
  // queue = role-based My Queue, pipeline = compact column board, calendar = studios.
  // Atlas (thumbnail-first grid) was removed by request — kept the AI thumbnail
  // pipeline on the asset detail hero, but no Atlas board surface.
  const [view, setView] = useState("pipeline");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [showCreateAsset, setShowCreateAsset] = useState(false);
  const searchRef = useRef(null);
  const isMobile = useIsMobile();
  const [mobileTab, setMobileTab] = useState("queue");

  useEffect(() => { loadData(); }, []);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { if (window.innerWidth < 768 && view === 'calendar') setView('pipeline'); }, []);

  const loadData = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const weekStart = new Date(Date.now() - 86400000 * new Date().getDay()).toISOString().split("T")[0];
      const weekEnd = new Date(Date.now() + 86400000 * (6 - new Date().getDay())).toISOString().split("T")[0];
      const [assetsRes, queueRes, statsRes, studiosRes, bookingsRes, shootsRes] = await Promise.all([
        api.get("/production/assets").catch(() => ({ data: [] })),
        api.get("/production/my-queue").catch(() => ({ data: null })),
        api.get("/production/stats").catch(() => ({ data: null })),
        api.get("/production/studios").catch(() => ({ data: [] })),
        api.get(`/production/bookings?week_start=${weekStart}`).catch(() => ({ data: [] })),
        api.get(`/shoots?date_from=${weekStart}&date_to=${weekEnd}`).catch(() => ({ data: [] })),
      ]);
      setAssets(assetsRes.data || []);
      setMyQueue(queueRes.data);
      setStats(statsRes.data);
      setStudios(studiosRes.data || []);
      setBookings(bookingsRes.data || []);
      setShoots(shootsRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Shoot booking modal state
  const [showShootBooking, setShowShootBooking] = useState(false);
  const [shootBookingAsset, setShootBookingAsset] = useState(null);
  const [shootForm, setShootForm] = useState({ studio_id: "", date: "", slots: [0, 1, 2, 3] });
  const resizeRef = useRef(null);
  const shootsRef = useRef([]);
  const clickGuard = useRef(false); // true while dragging/resizing so the tile's open-asset click doesn't fire
  useEffect(() => { shootsRef.current = shoots; }, [shoots]);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);

  // Admin-only: inline delete a duplicate from the pipeline column. Backend
  // already gates DELETE /production/assets/{id} on is_admin_user — this just
  // surfaces the action where the duplicate is visible (no detail-page detour).
  const isAdmin = (user?.role || "").toLowerCase() === "admin" || isSuperAdmin(user?.email);
  const deleteAsset = async (assetId, title) => {
    if (!window.confirm(`Delete "${title || 'this asset'}"? Soft-deletes; can be restored from DB.`)) return;
    try {
      await api.delete(`/production/assets/${assetId}`);
      toast.success("Asset deleted");
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed — admin only");
    }
  };

  const updateAssetStatus = async (assetId, newStatus) => {
    // Intercept: if advancing TO "Shoot Scheduled", prompt for studio booking
    if (newStatus === "Shoot Scheduled") {
      const asset = assets.find(a => a.asset_id === assetId);
      setShootBookingAsset(asset || { asset_id: assetId, title: "Asset" });
      const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];
      setShootForm({ studio_id: "", date: tomorrow, slots: [0, 1, 2, 3] });
      setShowShootBooking(true);
      return;
    }
    try {
      await api.put(`/production/assets/${assetId}`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const confirmShootBooking = async () => {
    if (!shootForm.date) { toast.error("Pick a shoot date"); return; }
    if (!shootBookingAsset) return;
    setBookingSubmitting(true);
    try {
      const sel = [...(shootForm.slots || [])].sort((a, b) => a - b);
      if (!sel.length) { toast.error("Pick at least one slot"); setBookingSubmitting(false); return; }
      const startH = SLOT_DEFS[sel[0]].s, endH = SLOT_DEFS[sel[sel.length - 1]].e; // min..max = contiguous
      await api.put(`/production/assets/${shootBookingAsset.asset_id}`, { status: "Shoot Scheduled" });
      await api.post("/shoots", {
        production_id: shootBookingAsset.production_id || shootBookingAsset.asset_id,
        title: shootBookingAsset.title,
        venue_type: shootForm.studio_id ? "studio" : "outdoor",
        studio_id: shootForm.studio_id || undefined,
        start_at: `${shootForm.date}T${String(startH).padStart(2, "0")}:00:00`,
        end_at: `${shootForm.date}T${String(endH).padStart(2, "0")}:00:00`,
      });
      toast.success("Shoot scheduled!");
      setShowShootBooking(false);
      setShootBookingAsset(null);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to schedule shoot"); }
    finally { setBookingSubmitting(false); }
  };

  // Persist a shoot's new studio/day/time. `startSlot` (0..3) is where it now
  // begins; its slot-count (duration) is preserved and clamped inside the day.
  const persistShoot = async (shoot, studioId, dateStr, startSlot) => {
    const count = Math.max(1, Math.round((hourOf(shoot.end_at) - hourOf(shoot.start_at)) / 3));
    const sB = Math.max(0, Math.min(startSlot, NSLOT - count));
    const startH = DAY_START + sB * 3, endH = startH + count * 3;
    const start_at = `${dateStr}T${hhmm(startH)}`, end_at = `${dateStr}T${hhmm(endH)}`;
    setShoots((prev) => prev.map((s) => s.shoot_id === shoot.shoot_id
      ? { ...s, studio_id: studioId, date: dateStr, start_at, end_at } : s));
    try {
      await api.put(`/shoots/${shoot.shoot_id}`, { studio_id: studioId, start_at, end_at });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't move — studio conflict?");
      loadData();
    }
  };

  // Resize a shoot to a new slot range (edge drag). slotA/slotB are boundary
  // indices 0..4; the shoot spans [min,max], min 1 slot.
  const resizeShoot = async (shoot, startB, endB) => {
    const sB = Math.max(0, Math.min(startB, NSLOT - 1));
    const eB = Math.max(sB + 1, Math.min(endB, NSLOT));
    const startH = DAY_START + sB * 3, endH = DAY_START + eB * 3;
    const start_at = `${shoot.date}T${hhmm(startH)}`, end_at = `${shoot.date}T${hhmm(endH)}`;
    try {
      await api.put(`/shoots/${shoot.shoot_id}`, { start_at, end_at });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't resize — studio conflict?");
      loadData();
    }
  };

  // Edge-resize via pointer drag. Live-updates the optimistic shoots array,
  // PUTs the final range on mouse-up.
  const startResize = (shoot, edge, e) => {
    e.stopPropagation(); e.preventDefault();
    const cell = e.currentTarget.closest("[data-shootcell]");
    if (!cell) return;
    clickGuard.current = true; // suppress the post-resize click → no navigation
    const rectTop = cell.getBoundingClientRect().top;
    resizeRef.current = { shootId: shoot.shoot_id, edge, rectTop };
    const onMove = (ev) => {
      const r = resizeRef.current; if (!r) return;
      const b = Math.max(0, Math.min(NSLOT, Math.round((ev.clientY - r.rectTop) / SLOT_PX)));
      setShoots((prev) => prev.map((s) => {
        if (s.shoot_id !== r.shootId) return s;
        let sB = Math.round((hourOf(s.start_at) - DAY_START) / 3);
        let eB = Math.round((hourOf(s.end_at) - DAY_START) / 3);
        if (r.edge === "bottom") eB = Math.max(sB + 1, Math.min(NSLOT, b));
        else sB = Math.max(0, Math.min(eB - 1, b));
        return { ...s, start_at: `${s.date}T${hhmm(DAY_START + sB * 3)}`, end_at: `${s.date}T${hhmm(DAY_START + eB * 3)}` };
      }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      const r = resizeRef.current; resizeRef.current = null;
      setTimeout(() => { clickGuard.current = false; }, 80); // clear after the synthetic click has passed
      if (!r) return;
      const sh = (shootsRef.current || []).find((s) => s.shoot_id === r.shootId);
      if (sh) resizeShoot(sh, Math.round((hourOf(sh.start_at) - DAY_START) / 3), Math.round((hourOf(sh.end_at) - DAY_START) / 3));
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // Distinct accounts present in the asset list — drives the client filter.
  // Sorted alphabetically so the dropdown is predictable.
  const assetAccounts = useMemo(() => {
    const m = new Map();
    assets.forEach((a) => {
      if (a.account_id) m.set(a.account_id, a.account_name || a.account_id);
    });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [assets]);

  // Effective go-live month: planned, fallback actual, then legacy go_live/live_at.
  const effMonth = (a) => String(
    a.planned_go_live || a.actual_go_live || (a.deadlines || {}).go_live_date || a.live_at || ""
  ).slice(0, 7);
  // Distinct months present, newest first, for the month filter dropdown.
  const monthOptions = useMemo(() => {
    const s = new Set();
    assets.forEach((a) => { const m = effMonth(a); if (m) s.add(m); });
    return Array.from(s).sort().reverse();
  }, [assets]);
  const fmtMonth = (m) => { try { const [y, mo] = m.split("-"); return new Date(y, mo - 1, 1).toLocaleString("en", { month: "short", year: "numeric" }); } catch { return m; } };

  // Filtered assets
  const displayed = useMemo(() => {
    let result = [...assets];
    if (typeFilter !== "all") result = result.filter(a => a.asset_type === typeFilter);
    if (accountFilter !== "all") result = result.filter(a => a.account_id === accountFilter);
    if (monthFilter !== "all") result = result.filter(a => effMonth(a) === monthFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(a => a.title?.toLowerCase().includes(q) || a.account_name?.toLowerCase().includes(q));
    }
    return result;
  }, [assets, typeFilter, accountFilter, monthFilter, search]);

  // Group by phase
  const byPhase = useMemo(() => {
    const phases = { pre: [], shoot: [], post: [], review: [], done: [] };
    displayed.forEach(a => {
      const phase = STATUS_PHASE[a.status] || "pre";
      phases[phase].push(a);
    });
    return phases;
  }, [displayed]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-[var(--t3)] text-xl font-heading">LOADING...</div></div>;

  // ═══════ MOBILE VIEW — IG STYLE PRODUCTION ═══════
  if (isMobile) {
    const needsAction = displayed.filter(a => {
      const phase = STATUS_PHASE[a.status] || '';
      return phase === 'review' || a.status === 'Revisions';
    });
    const inProd = displayed.filter(a => {
      const phase = STATUS_PHASE[a.status] || '';
      return phase === 'shoot' || phase === 'post';
    });
    const todayBookings = bookings.filter(b => b.date === new Date().toISOString().split('T')[0]);

    const ProdCard = ({ asset }) => {
      const phase = STATUS_PHASE[asset.status] || 'pre';
      const phaseColor = PHASE_COLORS[phase] || '#64748B';
      const typeLabel = ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type || 'Asset';
      const typeColor = ASSET_TYPE_COLORS[asset.asset_type] || '#64748B';
      const progress = phase === 'done' ? 100 : phase === 'review' ? 80 : phase === 'post' ? 65 : phase === 'shoot' ? 45 : 20;
      return (
        <div onClick={() => navigate(`/production/assets/${asset.asset_id}`)}
          style={{ background: '#FFF', borderRadius: 12, margin: '0 16px 10px', padding: 14, boxShadow: '0 1px 3px rgba(0,0,0,.08)', cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <div style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, fontWeight: 600, background: `${typeColor}20`, color: typeColor }}>{typeLabel.toUpperCase()}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', flex: 1 }}>{asset.title}</div>
          </div>
          <div style={{ fontSize: 11, color: '#8E8E8E', display: 'flex', gap: 10 }}>
            <span>{asset.account_name || asset.lead_name || ''}</span>
            <span style={{ color: phaseColor }}>{asset.status}</span>
          </div>
          <div style={{ height: 4, background: '#EFEFEF', borderRadius: 2, overflow: 'hidden', marginTop: 8 }}>
            <div style={{ height: '100%', borderRadius: 2, width: `${progress}%`, background: phaseColor }} />
          </div>
        </div>
      );
    };

    return (
      <div style={{ paddingBottom: 16 }}>
        {/* IG Chips */}
        <div className="ig-scroll" style={{ display: 'flex', flexWrap: 'nowrap', gap: 6, padding: '10px 16px', overflowX: 'auto', scrollbarWidth: 'none' }}>
          {[
            { key: 'queue', label: 'My Queue' },
            { key: 'pipeline', label: 'Pipeline' },
            { key: 'shoots', label: 'Shoots' },
            { key: 'studios', label: 'Studios' },
          ].map(c => (
            <button key={c.key} onClick={() => setMobileTab(c.key)}
              style={{ padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500, background: mobileTab === c.key ? '#262626' : '#EFEFEF', color: mobileTab === c.key ? '#FFF' : '#262626', border: 'none', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Queue */}
        {mobileTab === 'queue' && (
          <div style={{ padding: '4px 0' }}>
            {needsAction.length > 0 && <>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', padding: '12px 16px 6px' }}>Needs Action</div>
              {needsAction.map(a => <ProdCard key={a.asset_id} asset={a} />)}
            </>}
            {inProd.length > 0 && <>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', padding: '12px 16px 6px' }}>In Production</div>
              {inProd.map(a => <ProdCard key={a.asset_id} asset={a} />)}
            </>}
            {needsAction.length === 0 && inProd.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, margin: '0 16px', background: '#FFF', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                <p style={{ color: '#8E8E8E', fontSize: 13 }}>No items in your queue</p>
              </div>
            )}
          </div>
        )}

        {/* Pipeline — all assets */}
        {mobileTab === 'pipeline' && (
          <div style={{ padding: '4px 0' }}>
            {displayed.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, margin: '0 16px', background: '#FFF', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                <p style={{ color: '#8E8E8E', fontSize: 13 }}>No assets yet</p>
              </div>
            ) : displayed.slice(0, 20).map(a => <ProdCard key={a.asset_id} asset={a} />)}
          </div>
        )}

        {/* Shoots */}
        {mobileTab === 'shoots' && (
          <div style={{ padding: '4px 0' }}>
            {todayBookings.length > 0 && <>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', padding: '12px 16px 6px' }}>Today's Shoots</div>
              {todayBookings.map((b, i) => (
                <div key={i} style={{ background: '#FFF', borderRadius: 12, margin: '0 16px 10px', padding: 14, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, fontWeight: 600, background: '#FEF3C7', color: '#D97706' }}>SHOOT</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', flex: 1 }}>{b.asset_title || 'Shoot'}</div>
                  </div>
                  <div style={{ fontSize: 11, color: '#8E8E8E' }}>{b.studio_name || 'Studio'} · {b.shift || 'Full Day'}</div>
                </div>
              ))}
            </>}
            {todayBookings.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, margin: '0 16px', background: '#FFF', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                <p style={{ color: '#8E8E8E', fontSize: 13 }}>No shoots today</p>
              </div>
            )}
          </div>
        )}

        {/* Studios */}
        {mobileTab === 'studios' && (
          <div style={{ padding: '4px 0' }}>
            {studios.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, margin: '0 16px', background: '#FFF', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                <p style={{ color: '#8E8E8E', fontSize: 13 }}>No studios configured</p>
              </div>
            ) : studios.map((s, i) => (
              <div key={i} onClick={() => navigate('/production/studios')}
                style={{ background: '#FFF', borderRadius: 12, margin: '0 16px 10px', padding: 14, boxShadow: '0 1px 3px rgba(0,0,0,.08)', cursor: 'pointer' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', marginBottom: 4 }}>{s.name}</div>
                <div style={{ fontSize: 11, color: '#8E8E8E' }}>{s.location || ''} · {s.capacity || '?'} capacity</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Assets live 7+ days ago that still have no engagement KPIs — capture them.
  const weekAgo = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
  const kpisDue = (assets || []).filter((a) => {
    if (a.status !== "Live") return false;
    const er = (a.engagement_metrics || {}).er_rate;
    if (er != null && er !== "") return false;
    const live = String(a.live_at || (a.deadlines || {}).go_live_date || "").slice(0, 10);
    return live && live <= weekAgo;
  });

  // ═══════ DESKTOP VIEW ═══════
  return (
    <div className="max-w-[1200px] mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl text-[var(--t1)] tracking-wide">PRODUCTION</h1>
          <p className="text-xs text-[#888]">{assets.length} assets across {new Set(assets.map(a => a.account_id)).size} accounts</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate("/production/performance")} className="flex items-center gap-2 px-4 py-2 bg-[var(--s1)] border border-[var(--bdr)] text-[#999] text-xs rounded-lg hover:text-[var(--t1)] transition-colors">
            <BarChart3 size={14} /> Performance
          </button>
          <button onClick={() => navigate("/production/backfill")} className="flex items-center gap-2 px-4 py-2 bg-[var(--s1)] border border-[var(--bdr)] text-[#999] text-xs rounded-lg hover:text-[var(--t1)] transition-colors">
            <Upload size={14} /> Backfill
          </button>
          <button onClick={() => navigate("/production/backfill")}
            className="flex items-center gap-2 px-5 py-2.5 bg-[var(--r)] text-white text-[13px] font-bold rounded-full hover:bg-[var(--r)] transition-all font-heading">
            <Plus size={16} /> New Asset
          </button>
        </div>
      </div>

      {/* KPIs due — assets live 7+ days with no engagement metrics yet */}
      {kpisDue.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[.06] px-4 py-3">
          <div className="flex items-center gap-2 flex-wrap">
            <AlertTriangle size={15} className="text-amber-500" />
            <span className="text-[13px] text-[var(--t1)]">
              <span className="font-bold">{kpisDue.length}</span> asset{kpisDue.length > 1 ? "s" : ""} went live 7+ days ago and still need KPIs.
            </span>
            <button onClick={() => setShowKpiDue((v) => !v)} className="text-[12px] text-amber-600 hover:underline ml-1">
              {showKpiDue ? "hide" : "view"}
            </button>
          </div>
          {showKpiDue && (
            <div className="mt-2 flex flex-col gap-1">
              {kpisDue.slice(0, 30).map((a) => (
                <button key={a.asset_id} onClick={() => navigate(`/production/assets/${a.asset_id}`)}
                  className="flex items-center justify-between text-left text-[12px] py-1.5 px-2 rounded-lg hover:bg-[var(--s2)] transition-colors">
                  <span className="text-[var(--t1)] truncate">{a.title || "Untitled"}</span>
                  <span className="text-[var(--t3)] ml-3 flex-none">{a.account_name || ""} · live {String(a.live_at || (a.deadlines || {}).go_live_date || "").slice(0, 10)} →</span>
                </button>
              ))}
              {kpisDue.length > 30 && <span className="text-[11px] text-[var(--t3)] px-2">+{kpisDue.length - 30} more</span>}
            </div>
          )}
        </div>
      )}

      {/* Stats Strip */}
      {stats && (
        <div className="flex items-center gap-4 px-4 py-3 bg-[var(--s1)] border border-[var(--bdr)] rounded-xl dm-stats-strip">
          <div className="flex items-center gap-2 text-xs">
            <Film size={14} className="text-[#888]" />
            <span className="text-[#999]">Total</span>
            <span className="text-[var(--t1)] font-semibold">{stats.total_assets}</span>
          </div>
          <div className="w-px h-5 bg-[var(--s2)] dm-stats-divider" />
          {Object.entries(stats.by_type || {}).map(([type, count]) => (
            <div key={type} className="flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full" style={{ background: ASSET_TYPE_COLORS[type] || "#666" }} />
              <span className="text-[#999]">{ASSET_TYPE_LABELS[type] || type}</span>
              <span className="text-[var(--t3)] font-semibold">{count}</span>
            </div>
          ))}
          <div className="w-px h-5 bg-[var(--s2)] dm-stats-divider" />
          <div className="flex items-center gap-1.5 text-xs">
            <CheckCircle2 size={12} className="text-green-400" />
            <span className="text-green-400">{stats.live} live</span>
          </div>
          {stats.overdue > 0 && (
            <div className="flex items-center gap-1.5 text-xs">
              <AlertTriangle size={12} className="text-red-400" />
              <span className="text-red-400">{stats.overdue} overdue</span>
            </div>
          )}
          <div className="ml-auto flex items-center gap-1.5 text-xs">
            <Calendar size={12} className="text-amber-400" />
            <span className="text-amber-400">{stats.todays_bookings} shoots today</span>
          </div>
        </div>
      )}

      {/* View Toggle + Search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-[320px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
          <input ref={searchRef} type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search assets, accounts..."
            className="w-full pl-10 pr-4 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-white text-[13px] placeholder:text-[var(--t3)] focus:outline-none focus:border-[#E31937] transition-all" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-xs text-[#999] outline-none cursor-pointer">
          <option value="all">All Types</option>
          {Object.entries(ASSET_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={accountFilter} onChange={e => setAccountFilter(e.target.value)}
          className="px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-xs text-[#999] outline-none cursor-pointer max-w-[180px]">
          <option value="all">All Clients</option>
          {assetAccounts.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
          title="Filter by planned go-live month (falls back to actual)"
          className="px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-xs text-[#999] outline-none cursor-pointer">
          <option value="all">All Months</option>
          {monthOptions.map(m => <option key={m} value={m}>{fmtMonth(m)}</option>)}
        </select>
        <div className="flex gap-1.5 p-1 bg-[var(--s1)] rounded-lg border border-[var(--bdr)] ml-auto">
          {[
            { key: "queue", icon: Flame, label: "My Queue" },
            { key: "pipeline", icon: BarChart3, label: "Pipeline" },
            { key: "calendar", icon: Calendar, label: "Calendar" },
          ].map(v => (
            <button key={v.key} onClick={() => setView(v.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors ${view === v.key ? "bg-[var(--s2)] text-white" : "text-[#888] hover:text-[var(--t1)]"}`}>
              <v.icon size={14} /> {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* ═══════ MY QUEUE VIEW ═══════ */}
      {view === "queue" && myQueue && (
        <div className="space-y-4">
          {/* Needs My Action */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Flame size={14} className="text-red-400" />
              <span className="text-xs font-semibold text-[#999] font-heading tracking-wider">NEEDS YOUR ACTION</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">{(myQueue.needs_my_action || []).length}</span>
            </div>
            {(myQueue.needs_my_action || []).length === 0 ? (
              <div className="text-center py-8 rounded-xl border border-dashed border-[var(--bdr)] bg-[var(--s1)] text-xs text-[#555]">
                Nothing needs your action right now 🎉
              </div>
            ) : (
              <div className="space-y-2">
                {(myQueue.needs_my_action || []).map(a => (
                  <AssetQueueCard key={a.asset_id} asset={a} navigate={navigate} onStatusChange={updateAssetStatus} isAdmin={isAdmin} onDelete={deleteAsset} />
                ))}
              </div>
            )}
          </div>
          {/* Waiting on Others */}
          {(myQueue.waiting_on_others || []).length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Clock size={14} className="text-[#888]" />
                <span className="text-xs font-semibold text-[#888] font-heading tracking-wider">WAITING ON OTHERS</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--s2)] text-[#888]">{(myQueue.waiting_on_others || []).length}</span>
              </div>
              <div className="space-y-2">
                {(myQueue.waiting_on_others || []).slice(0, 10).map(a => (
                  <div key={a.asset_id} onClick={() => navigate(`/production/assets/${a.asset_id}`)}
                    className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-[var(--bdr)] bg-[var(--s1)] cursor-pointer hover:bg-[var(--s1)] transition-colors">
                    <span className="w-2 h-2 rounded-full" style={{ background: ASSET_TYPE_COLORS[a.asset_type] || "#666" }} />
                    <span className="text-sm text-[var(--t1)]/60 flex-1 truncate">{a.title}</span>
                    <span className="text-xs px-2 py-0.5 rounded" style={{ background: (PHASE_COLORS[STATUS_PHASE[a.status]] || "#666") + "18", color: PHASE_COLORS[STATUS_PHASE[a.status]] || "#666" }}>
                      {a.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════ PIPELINE VIEW ═══════ */}
      {view === "pipeline" && (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {Object.entries(byPhase).map(([phase, phaseAssets]) => (
            <div key={phase} className="flex-shrink-0 w-full sm:w-[260px] bg-[var(--s1)] border border-[var(--bdr)] rounded-xl flex flex-col max-h-[70vh]">
              <div className="flex items-center justify-between px-3 py-3 border-b border-[var(--bdr)]">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: PHASE_COLORS[phase] }} />
                  <span className="text-xs font-semibold tracking-wider font-heading" style={{ color: PHASE_COLORS[phase] }}>{PHASE_LABELS[phase]}</span>
                </div>
                <span className="text-xs text-[#666] bg-[var(--s2)] px-2 py-0.5 rounded-full">{phaseAssets.length}</span>
              </div>
              <div className="flex-1 p-3 space-y-2 overflow-y-auto">
                {phaseAssets.length === 0 && (
                  <div className="text-center py-8 text-xs text-[#555]">No assets</div>
                )}
                {phaseAssets.map(a => (
                  <div key={a.asset_id} onClick={() => navigate(`/production/assets/${a.asset_id}`)}
                    className={`bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-3 cursor-pointer transition-all hover:border-[var(--bdrh)] hover:-translate-y-0.5 ${a.is_overdue ? "border-red-500/15" : ""}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: ASSET_TYPE_COLORS[a.asset_type] }} />
                      <span className="text-xs text-[#888]">{ASSET_TYPE_LABELS[a.asset_type]}</span>
                    </div>
                    <p className="text-sm font-medium text-white/90 leading-tight mb-2">{a.title}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs px-2 py-0.5 rounded" style={{ background: (PHASE_COLORS[STATUS_PHASE[a.status]] || "#666") + "18", color: PHASE_COLORS[STATUS_PHASE[a.status]] || "#666" }}>
                        {a.status}
                      </span>
                      {a.team_names && Object.values(a.team_names)[0] && (
                        <span className="text-xs text-[#666]">{Object.values(a.team_names)[0]}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══════ CALENDAR VIEW (Today's Studio Bookings) ═══════ */}
      {view === "calendar" && (
        window.innerWidth < 768 ? (
          <div style={{ padding: 16, color: 'var(--t3)', fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'center' }}>
            Studio calendar is optimized for desktop.<br/>Use Pipeline view on mobile.
          </div>
        ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-[#999] font-heading tracking-wider">THIS WEEK'S SHOOTS <span className="text-[#555] font-normal normal-case tracking-normal">· drag to reschedule</span></span>
            <button onClick={() => navigate("/production/studios")} className="text-xs text-[#888] hover:text-[var(--t1)] transition-colors">Manage Studios →</button>
          </div>
          {/* Studio × day grid — each cell is the 9a–9p day in 4 slots; shoots
              fill their slot range, drag to move (studio/day/slot), edge-drag to resize */}
          <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl overflow-hidden">
            <div style={{ display: "grid", gridTemplateColumns: "130px 28px repeat(7, minmax(0,1fr))" }} className="border-b border-[var(--bdr)]">
              <div className="px-3 py-2 text-xs text-[#888] font-heading">STUDIO</div>
              <div />
              {Array.from({ length: 7 }, (_, i) => {
                const d = new Date(); d.setDate(d.getDate() - d.getDay() + i);
                const isToday = d.toDateString() === new Date().toDateString();
                return (
                  <div key={i} className={`px-1 py-2 text-center text-xs border-l border-[var(--bdr)] ${isToday ? "bg-[var(--r)]/5 text-[#E31937] font-bold" : "text-[#666]"}`}>
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()]} {d.getDate()}
                  </div>
                );
              })}
            </div>
            {studios.filter(s => s.status === "active").map(studio => (
              <div key={studio.studio_id} style={{ display: "grid", gridTemplateColumns: "130px 28px repeat(7, minmax(0,1fr))" }} className="border-b border-[var(--bdr)]">
                <div className="px-3 py-2 text-xs text-[var(--t2)] font-medium">{studio.name}
                  <span className="text-xs text-[#555] block">Floor {studio.floor}</span>
                </div>
                <div style={{ position: "relative", height: CAL_CELL }}>
                  {RAIL_MARKS.map(h => (
                    <div key={h} style={{ position: "absolute", right: 3, top: Math.min((h - DAY_START) / (DAY_END - DAY_START) * CAL_CELL, CAL_CELL - 10) }} className="text-[9px] text-[#555] tabular-nums">{hourLabel(h)}</div>
                  ))}
                </div>
                {Array.from({ length: 7 }, (_, i) => {
                  const d = new Date(); d.setDate(d.getDate() - d.getDay() + i);
                  const dateStr = d.toISOString().split("T")[0];
                  const dayShoots = shoots.filter(s => s.studio_id === studio.studio_id && s.date === dateStr);
                  const dayBookings = bookings.filter(b => b.studio_id === studio.studio_id && b.date === dateStr);
                  const isDrop = dragShoot && !(dragShoot.studio_id === studio.studio_id && dragShoot.date === dateStr);
                  return (
                    <div key={i} data-shootcell
                      onDragOver={(e) => { if (dragShoot) e.preventDefault(); }}
                      onDrop={(e) => { e.preventDefault(); if (dragShoot) { const r = e.currentTarget.getBoundingClientRect(); const slot = Math.floor((e.clientY - r.top) / SLOT_PX); persistShoot(dragShoot, studio.studio_id, dateStr, slot); setDragShoot(null); } }}
                      style={{ position: "relative", height: CAL_CELL }}
                      className={`border-l border-[var(--bdr)] ${isDrop ? "bg-[#E31937]/10" : ""}`}>
                      {[1, 2, 3].map(k => (
                        <div key={k} style={{ position: "absolute", left: 0, right: 0, top: k * SLOT_PX, borderTop: "1px dashed var(--bdr)", opacity: 0.5 }} />
                      ))}
                      {dayShoots.map(s => {
                        const sh = hourOf(s.start_at), eh = hourOf(s.end_at);
                        const top = (sh - DAY_START) / (DAY_END - DAY_START) * CAL_CELL;
                        const h = (eh - sh) / (DAY_END - DAY_START) * CAL_CELL;
                        return (
                          <div key={s.shoot_id} draggable
                            onDragStart={() => { clickGuard.current = true; setDragShoot(s); }}
                            onDragEnd={() => { setDragShoot(null); setTimeout(() => { clickGuard.current = false; }, 80); }}
                            onClick={() => { if (clickGuard.current) return; if (s.production_id) navigate(`/production/assets/${s.production_id}`); }}
                            title={`${(s.start_at || "").slice(11, 16)}–${(s.end_at || "").slice(11, 16)} · ${s.title || ""} — drag to move, edge to resize, click to open`}
                            style={{ position: "absolute", left: 3, right: 3, top: top + 1, height: Math.max(h - 2, 14), overflow: "hidden", opacity: dragShoot?.shoot_id === s.shoot_id ? 0.4 : 1 }}
                            className="rounded bg-[#E31937]/15 hover:bg-[#E31937]/25 cursor-grab active:cursor-grabbing border-l-2 border-[#E31937]">
                            <div style={{ position: "absolute", inset: 0, padding: "2px 5px", pointerEvents: "none" }}>
                              <span className="text-[10px] leading-tight text-[#ff7a8a] font-medium" style={{ display: "-webkit-box", WebkitLineClamp: h < 34 ? 1 : h < 60 ? 2 : 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{s.title || "Shoot"}</span>
                            </div>
                            <div onMouseDown={(e) => startResize(s, "top", e)} style={{ position: "absolute", top: 0, left: 0, right: 0, height: 7, cursor: "ns-resize", zIndex: 2 }} />
                            <div onMouseDown={(e) => startResize(s, "bottom", e)} style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 7, cursor: "ns-resize", zIndex: 2 }} />
                          </div>
                        );
                      })}
                      {dayBookings.map(b => {
                        const sh = b.start_hour || 9, eh = b.end_hour || (sh + 1);
                        const top = (sh - DAY_START) / (DAY_END - DAY_START) * CAL_CELL;
                        const h = (eh - sh) / (DAY_END - DAY_START) * CAL_CELL;
                        return (
                          <div key={b.booking_id} title={`${sh}:00-${eh}:00 — ${b.title || b.account_name || ""}`}
                            style={{ position: "absolute", left: 3, right: 3, top: top + 1, height: Math.max(h - 2, 14), overflow: "hidden" }}
                            className="rounded bg-amber-500/10 border-l-2 border-amber-500/50 px-1">
                            <span className="text-[10px] text-amber-400">{b.title || b.account_name || "Booked"}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
            {studios.filter(s => s.status === "active").length === 0 && (
              <div className="text-center py-8 text-xs text-[#555]">No studios configured. <button onClick={() => navigate("/production/studios")} className="text-[#E31937]">Set up studios →</button></div>
            )}
          </div>
        </div>
        )
      )}

      {/* Create Asset Modal */}
      {showCreateAsset && <CreateAssetModal onClose={() => setShowCreateAsset(false)} onCreated={() => { setShowCreateAsset(false); loadData(); }} />}

      {/* ═══════ SHOOT BOOKING MODAL ═══════ */}
      {showShootBooking && shootBookingAsset && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: "rgba(0,0,0,.7)", backdropFilter: "blur(8px)" }}>
          <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-2xl w-full max-w-[min(90vw,420px)] mx-4 p-6" style={{ boxShadow: "0 24px 80px rgba(0,0,0,.6)" }}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center"><Calendar size={16} className="text-amber-400" /></div>
                <div>
                  <h2 className="text-[15px] font-bold text-white font-heading">Schedule Shoot</h2>
                  <p className="text-xs text-[#666]">{shootBookingAsset.title}</p>
                </div>
              </div>
              <button onClick={() => { setShowShootBooking(false); setShootBookingAsset(null); }} className="text-[#555] hover:text-red-400 hover:bg-red-500/10 transition-all p-1.5 rounded-lg"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#888] block mb-1.5 font-heading tracking-wider">STUDIO / VENUE</label>
                <select value={shootForm.studio_id} onChange={e => setShootForm({ ...shootForm, studio_id: e.target.value })}
                  className="w-full px-3 py-2.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-[13px] text-white focus:outline-none focus:border-amber-500/40 cursor-pointer">
                  <option value="">Outdoor / Client Site (no studio)</option>
                  {studios.filter(s => s.status === "active").map(s => (
                    <option key={s.studio_id} value={s.studio_id}>{s.name}{s.floor ? ` — Floor ${s.floor}` : ""}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#888] block mb-1.5 font-heading tracking-wider">SHOOT DATE</label>
                <input type="date" value={shootForm.date} onChange={e => setShootForm({ ...shootForm, date: e.target.value })}
                  className="w-full px-3 py-2.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-[13px] text-white focus:outline-none focus:border-amber-500/40 cursor-pointer" />
              </div>
              <div>
                <label className="text-xs text-[#888] block mb-1.5 font-heading tracking-wider">TIME SLOTS <span className="text-[#555] font-normal normal-case tracking-normal">· tap 1–4 (contiguous)</span></label>
                <div className="grid grid-cols-4 gap-2">
                  {SLOT_DEFS.map(slot => {
                    const sel = [...(shootForm.slots || [])];
                    const lo = sel.length ? Math.min(...sel) : -1, hi = sel.length ? Math.max(...sel) : -1;
                    const inRange = slot.k >= lo && slot.k <= hi; // min..max = the booked span
                    return (
                      <button key={slot.k} onClick={() => {
                        let next;
                        if (!sel.length) next = [slot.k];
                        else if (slot.k < lo) next = [slot.k, hi];
                        else if (slot.k > hi) next = [lo, slot.k];
                        else if (slot.k === lo && slot.k === hi) next = [];           // toggle off the only one
                        else if (slot.k === lo) next = [lo + 1, hi];                  // trim from top
                        else if (slot.k === hi) next = [lo, hi - 1];                  // trim from bottom
                        else next = [slot.k];                                          // clicking inside → collapse to it
                        setShootForm({ ...shootForm, slots: next });
                      }}
                        className={`py-2.5 rounded-lg border text-center transition-all ${
                          inRange ? "bg-amber-500/15 border-amber-500/40 text-amber-400"
                                  : "border-[var(--bdr)] text-[#666] hover:text-[#999]"
                        }`}>
                        <span className="text-[11px] font-semibold block">{slot.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => { setShowShootBooking(false); setShootBookingAsset(null); }}
                className="flex-1 py-2.5 border border-[var(--bdr)] text-[#999] rounded-xl text-xs font-semibold hover:text-[var(--t1)] transition-colors">Cancel</button>
              <button onClick={confirmShootBooking} disabled={bookingSubmitting}
                className="flex-1 py-2.5 bg-amber-500/15 text-amber-400 border border-amber-500/25 rounded-xl text-xs font-bold hover:bg-amber-500/25 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                <Calendar size={14} /> {bookingSubmitting ? "Scheduling..." : "Schedule Shoot"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   ASSET QUEUE CARD
   ═══════════════════════════════════════ */
function AssetQueueCard({ asset, navigate, onStatusChange, isAdmin = false, onDelete }) {
  const pipeline = asset.pipeline || [];
  const currentIdx = pipeline.indexOf(asset.status);
  const nextStatus = currentIdx >= 0 && currentIdx < pipeline.length - 1 ? pipeline[currentIdx + 1] : null;
  const phase = STATUS_PHASE[asset.status] || "pre";

  return (
    <div onClick={() => navigate(`/production/assets/${asset.asset_id}`)}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors cursor-pointer group
      ${asset.is_overdue ? "border-red-500/15 bg-red-500/[.02] hover:bg-red-500/[.04]" : "border-[var(--bdr)] bg-[var(--s1)] hover:bg-[var(--s1)]"}`}>
      {/* Type indicator */}
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: (ASSET_TYPE_COLORS[asset.asset_type] || "#666") + "15" }}>
        <Film size={14} style={{ color: ASSET_TYPE_COLORS[asset.asset_type] || "#666" }} />
      </div>
      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-white/90 truncate">{asset.title}</p>
        <div className="flex items-center gap-2 text-xs mt-0.5">
          <span style={{ color: ASSET_TYPE_COLORS[asset.asset_type] }}>{ASSET_TYPE_LABELS[asset.asset_type]}</span>
          {asset.my_roles?.length > 0 && <span className="text-[#666]">as {asset.my_roles.join(", ")}</span>}
          {asset.is_overdue && <span className="text-red-400 font-semibold">OVERDUE</span>}
        </div>
      </div>
      {/* Current status */}
      <span className="text-xs px-2.5 py-1 rounded-md flex-shrink-0"
        style={{ background: (PHASE_COLORS[phase] || "#666") + "18", color: PHASE_COLORS[phase] || "#666" }}>
        {asset.status}
      </span>
      {/* Advance button */}
      {nextStatus && (
        <button onClick={(e) => { e.stopPropagation(); onStatusChange(asset.asset_id, nextStatus); }}
          className="text-xs px-3 py-1.5 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors flex items-center gap-1 opacity-0 group-hover:opacity-100 flex-shrink-0">
          → {nextStatus}
        </button>
      )}
      {/* Admin-only inline delete — for clearing duplicate assets from a column
          without detouring to the detail page. Backend already gates DELETE on
          is_admin_user, but we hide the button entirely from non-admins. */}
      {isAdmin && onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(asset.asset_id, asset.title); }}
          title="Delete asset (admin only)"
          className="p-1.5 rounded-lg text-[#666] hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   CREATE ASSET MODAL
   ═══════════════════════════════════════ */
function CreateAssetModal({ onClose, onCreated }) {
  const [accounts, setAccounts] = useState([]);
  const [allBriefs, setAllBriefs] = useState([]);

  useEffect(() => {
    // /accounts only returns Won leads — but the user can upload a brief
    // against any-stage lead and then expect to attach a production asset
    // to it. Using /leads (all stages, deleted_at filter applied server-
    // side) makes the dropdown match the brief catalogue.
    Promise.all([
      api.get("/leads").catch(() => ({ data: [] })),
      api.get("/bizdev/briefs").catch(() => ({ data: [] })),
    ]).then(([a, b]) => { setAccounts(a.data || []); setAllBriefs(b.data || []); });
  }, []);

  const initialState = {
    title: "",
    account_id: "",
    brief_id: "",
    asset_type: "reel",
    platform: "",
    planned_go_live: "",
    files: { brief_link: "", script_link: "", treatment_link: "", frame_io_link: "" },
    team: {},
  };

  const filteredBriefs = (form) =>
    form.account_id ? allBriefs.filter((b) => b.lead_id === form.account_id) : allBriefs;

  const inputCls = "w-full px-3 py-2.5 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-white text-[13px] placeholder:text-[#555] focus:outline-none focus:border-[#E31937]";
  const labelCls = "block text-[11px] font-semibold text-[var(--t2)] mb-1.5 tracking-wider uppercase";

  const submit = async (form, minimum = false) => {
    try {
      const payload = minimum
        ? {
            title: (form.title || "").trim(),
            account_id: form.account_id,
            brief_id: form.brief_id,
            asset_type: form.asset_type || "reel",
            platform: "",
            planned_go_live: form.planned_go_live,
            files: { brief_link: "", script_link: "", treatment_link: "", frame_io_link: "" },
            team: { producer: form.team?.producer },
          }
        : form;
      await api.post("/production/assets", payload);
      toast.success(minimum ? "Asset created — fill the rest later" : "Asset created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create asset");
      throw e;
    }
  };

  const steps = [
    {
      key: "essentials",
      label: "Essentials",
      validate: (form) => {
        const errs = {};
        if (!form.title?.trim()) errs.title = "Title is required";
        if (!form.account_id) errs.account_id = "Pick an account";
        if (!form.brief_id) errs.brief_id = "Pick a brief";
        if (!form.planned_go_live) errs.planned_go_live = "Planned go-live is required";
        if (!form.team?.producer) errs.producer = "Producer is required";
        return errs;
      },
      render: ({ form, setForm, errors }) => (
        <div className="space-y-4">
          <p className="text-[12px] text-[#888]">
            Title, who it's for, which brief, and who's producing. That's enough to ship — fill type, platform, files, and the rest of the team in the next steps.
          </p>
          <div>
            <label className={labelCls}>Title *</label>
            <input
              autoFocus
              className={inputCls}
              value={form.title}
              onChange={(e) => setForm({ title: e.target.value })}
              placeholder="e.g. Myntra April Reel #3 — Summer Collection"
            />
            {errors?.title && <p className="text-[11px] text-red-400 mt-1">{errors.title}</p>}
          </div>
          <div>
            <label className={labelCls}>Planned go-live *</label>
            <input
              type="date"
              className={inputCls}
              style={{ colorScheme: "dark" }}
              value={form.planned_go_live || ""}
              onChange={(e) => setForm({ planned_go_live: e.target.value })}
            />
            <p className="text-[11px] text-[#666] mt-1">Locks once set — only an admin can change it later.</p>
            {errors?.planned_go_live && <p className="text-[11px] text-red-400 mt-1">{errors.planned_go_live}</p>}
          </div>
          <div>
            <label className={labelCls}>Account / Lead *</label>
            <select
              className={inputCls}
              value={form.account_id}
              onChange={(e) => setForm({ account_id: e.target.value, brief_id: "" })}
            >
              <option value="">Select account…</option>
              {accounts.map((a) => (
                <option key={a.lead_id} value={a.lead_id}>{a.company_name}</option>
              ))}
            </select>
            {errors?.account_id && <p className="text-[11px] text-red-400 mt-1">{errors.account_id}</p>}
          </div>
          <div>
            <label className={labelCls}>Brief *</label>
            <select
              className={inputCls}
              value={form.brief_id}
              onChange={(e) => setForm({ brief_id: e.target.value })}
              disabled={!form.account_id}
            >
              <option value="">{form.account_id ? "Select brief…" : "Pick an account first"}</option>
              {filteredBriefs(form).map((b) => (
                <option key={b.brief_id} value={b.brief_id}>{b.title}</option>
              ))}
            </select>
            {errors?.brief_id && <p className="text-[11px] text-red-400 mt-1">{errors.brief_id}</p>}
          </div>
          <div>
            <label className={labelCls}>Producer *</label>
            <UserAutocomplete
              value={form.team?.producer || ""}
              onChange={(v) => setForm({ team: { ...(form.team || {}), producer: v } })}
              placeholder="Who's producing?"
              required
            />
            {errors?.producer && <p className="text-[11px] text-red-400 mt-1">{errors.producer}</p>}
          </div>
        </div>
      ),
    },
    {
      key: "type",
      label: "Type & Platform",
      optional: true,
      render: ({ form, setForm }) => (
        <div className="space-y-4">
          <p className="text-[12px] text-[#888]">
            Asset type drives the pipeline (reel ≠ static workflow). Platform tells us where it'll run — used by the live URL guard later.
          </p>
          <div>
            <label className={labelCls}>Type</label>
            <select
              className={inputCls}
              value={form.asset_type}
              onChange={(e) => setForm({ asset_type: e.target.value })}
            >
              {Object.entries(ASSET_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Platform</label>
            <select
              className={inputCls}
              value={form.platform}
              onChange={(e) => setForm({ platform: e.target.value })}
            >
              <option value="">Select platform…</option>
              <option value="instagram">Instagram</option>
              <option value="youtube">YouTube</option>
              <option value="meta_ads">Meta Ads</option>
              <option value="google_ads">Google Ads</option>
              <option value="mainline">Mainline / TV</option>
              <option value="digital">Digital</option>
            </select>
          </div>
        </div>
      ),
    },
    {
      key: "team",
      label: "Team",
      optional: true,
      render: ({ form, setForm }) => (
        <div className="space-y-4">
          <p className="text-[12px] text-[#888]">
            Optional — most fields inherit from the brief if left blank. Fill what's locked in already; the rest can come later.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {["creative_lead", "writer", "director", "editor", "designer"].map((role) => (
              <UserAutocomplete
                key={role}
                value={form.team?.[role] || ""}
                onChange={(v) => setForm({ team: { ...(form.team || {}), [role]: v } })}
                label={role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                placeholder={`Assign ${role.replace(/_/g, " ")}…`}
              />
            ))}
          </div>
        </div>
      ),
    },
    {
      key: "files",
      label: "Files",
      optional: true,
      render: ({ form, setForm }) => (
        <div className="space-y-3">
          <p className="text-[12px] text-[#888]">
            Optional — paste any links you have now. You can add the rest from the asset detail page once it exists.
          </p>
          {[
            { key: "brief_link", label: "Brief / reference link", placeholder: "Google Doc / Notion link…" },
            { key: "script_link", label: "Script link", placeholder: "Script Google Doc…" },
            { key: "treatment_link", label: "Treatment deck link", placeholder: "Treatment deck link…" },
            { key: "frame_io_link", label: "Frame.io / edit link", placeholder: "Frame.io project link…" },
          ].map((f) => (
            <div key={f.key}>
              <label className={labelCls}>{f.label}</label>
              <input
                className={inputCls}
                placeholder={f.placeholder}
                value={form.files?.[f.key] || ""}
                onChange={(e) => setForm({ files: { ...(form.files || {}), [f.key]: e.target.value } })}
              />
            </div>
          ))}
        </div>
      ),
    },
  ];

  return (
    <FormCarousel
      title="New Asset"
      draftKey="asset"
      finalLabel="Create Asset"
      initialState={initialState}
      steps={steps}
      onSaveMinimum={(form) => submit(form, true)}
      onSaveFull={(form) => submit(form, false)}
      onClose={onClose}
    />
  );
}
